import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CheckmarkCircle02Icon } from '@hugeicons-pro/core-solid-rounded';
import { HugeiconsIcon } from '@hugeicons/react';
import { useDisableCandidateFormActions } from 'contexts/contexts';
import {
  getDependentsFromValues,
  runHiddenDependents,
} from 'helpers/candidate_application/dependent-field-processing';
import { buildFieldPath, getFieldLocation } from 'helpers/candidate_application/field-location';
import { buildNavPath } from 'helpers/candidate_application/form-page-helpers';
import { hasAnyChange } from 'helpers/compare-objects';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { getValidatorDependencyCodes } from 'helpers/validators/function-validators';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import { ConfirmDetailsSheet } from 'components/ExtractedFieldsDisplay/ConfirmDetailsSheet';
import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';
import {
  Address,
  Checkbox,
  DateSelector,
  DocumentUpload,
  Email,
  MultipleChoice,
  Select,
  Text,
  Textarea,
} from 'components/FormComponents';
import { CustomToast } from 'components/Toast/CustomToast';

import {
  saveCandidateChecksValues,
  selectCandidateApplication,
  updateCandidateValues,
  updateHiddenValues,
  updateIdUploadDetail,
} from 'api/candidate-application-slice';

export const ExtractedFieldsDisplay = ({
  displayFields = [],
  idValues = {},
  brand,
  uploadId = null,
  documentTitle = '',
  confirmedByCand = false,
  onConfirm = () => {},
}) => {
  const { t } = useTranslation();
  const brandingStyleVariables = useBrandingStyleVariables(brand);
  const dispatch = useDispatch();
  const {
    candidateFields: {
      candidateValues,
      hiddenValues,
      hiddenDependencyCodes = {},
      hiddenFields = {},
      fieldPathIndex = {},
      sections = [],
    },
  } = useSelector(selectCandidateApplication);
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const admin = searchParams.get('admin');
  const { registerPendingConfirmation, unregisterPendingConfirmation, isActiveConfirmation } =
    useDisableCandidateFormActions();

  // Generate a unique identifier for this confirmation instance
  // Use uploadId if available, otherwise generate a unique one
  const confirmationIdRef = useRef(
    uploadId ? `upload-${uploadId}` : `extracted-${Date.now()}-${Math.random()}`,
  );

  // Update confirmation ID when uploadId changes
  useEffect(() => {
    const oldId = confirmationIdRef.current;
    const newId = uploadId ? `upload-${uploadId}` : `extracted-${Date.now()}-${Math.random()}`;

    // If the ID changed, unregister the old one, update the ref, and register the new one
    if (oldId !== newId) {
      unregisterPendingConfirmation(oldId);
      confirmationIdRef.current = newId;
      // Only re-register when confirmation is still outstanding — remounting an already
      // confirmed panel (View details) must not flip the list back to Action Needed.
      if (displayFields?.length > 0 && !confirmedByCand) {
        registerPendingConfirmation(newId);
      }
    }
  }, [
    uploadId,
    confirmedByCand,
    unregisterPendingConfirmation,
    registerPendingConfirmation,
    displayFields?.length,
  ]);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {},
  });

  const didInitRef = useRef(false);

  const { getUpload, signUpload, createUpload, deleteUpload } =
    useCandidateApplicationUploadActions(token);

  const handlers = useMemo(
    () => ({
      getCandidateUpload: getUpload,
      signCandidateUpload: signUpload,
      createCandidateUpload: createUpload,
      deleteCandidateUpload: deleteUpload,
    }),
    [getUpload, signUpload, createUpload, deleteUpload],
  );

  // Watch all form values
  const fieldValues = watch();

  // Create merged getValues that includes candidateValues for validators to access (e.g., dob)
  const getMergedValues = useCallback(
    () => ({
      ...candidateValues,
      ...hiddenValues,
      ...watch(), // local form values take precedence
    }),
    [candidateValues, hiddenValues, watch],
  );

  // State to track idUploadDetailsField mappings for each field
  const [idUploadDetailMappings, setIdUploadDetailMappings] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(confirmedByCand);
  // Auto-open as soon as this mounts eligible-but-unconfirmed (fresh verify or reload/return).
  const [sheetOpen, setSheetOpen] = useState(!confirmedByCand);
  const previousValuesRef = useRef(fieldValues);
  const valuesBaselineReadyRef = useRef(false);
  const previousConfirmedByCandRef = useRef(confirmedByCand);

  // React to confirmedByCand going false too, not just true, so an un-remounted parent can reopen it.
  useEffect(() => {
    const wasConfirmed = previousConfirmedByCandRef.current;
    previousConfirmedByCandRef.current = confirmedByCand;
    if (confirmedByCand) {
      setIsConfirmed(true);
    } else if (wasConfirmed) {
      setIsConfirmed(false);
      setSheetOpen(true);
    }
  }, [confirmedByCand]);

  // Reset confirmation only on real user edits. Remounting / form hydration ({} → seeded
  // values) must not clear an already-confirmed state — that was flipping the list to
  // Action Needed whenever "View details" was expanded.
  useEffect(() => {
    if (!valuesBaselineReadyRef.current) {
      previousValuesRef.current = fieldValues;
      if (didInitRef.current) {
        valuesBaselineReadyRef.current = true;
      }
      return;
    }
    if (isConfirmed) {
      const hasChanged = JSON.stringify(previousValuesRef.current) !== JSON.stringify(fieldValues);
      if (hasChanged) {
        setIsConfirmed(false);
      }
    }
    previousValuesRef.current = fieldValues;
  }, [fieldValues, isConfirmed]);

  // Helper function to determine if component needs controlled input
  const getComponentProps = useCallback(
    (value, question) => {
      // Components that need Controller (controlledInput: true)
      const controlledComponents = [
        'select',
        'multiple_choice',
        'date_selector',
        'upload',
        'checkbox',
      ];

      return {
        controlledInput: controlledComponents.includes(question?.type),
        children:
          question?.options?.map(option => (
            <Select.Item key={option.value} value={option.value}>
              {option.name || option.label}
            </Select.Item>
          )) || [],
        defaultValue: question?.defaultValue,
        getUpload: handlers.getCandidateUpload,
        signUpload: handlers.signCandidateUpload,
        createUpload: handlers.createCandidateUpload,
        deleteUpload: handlers.deleteCandidateUpload,
        noErrorMessage: true,
      };
    },
    [handlers],
  );

  // Memoize component map to prevent recreation on every render
  const localComponentMap = useMemo(
    () => ({
      short_text: Text,
      long_text: Textarea,
      select: Select,
      date_selector: DateSelector,
      multiple_choice: MultipleChoice,
      checkbox: Checkbox,
      email: Email,
      address: Address,
      upload: DocumentUpload,
    }),
    [],
  );

  // Field value mapping configuration
  const fieldValueMappings = useMemo(
    () => ({
      passport_nationality: 'passport_nationality_alpha2',
      // Add other field mappings here as needed
    }),
    [],
  );

  // Memoize helper function to prevent recreation on every render
  const getFieldValue = useCallback(
    (fieldCode, baseCode) => {
      // Combine candidateValues and hiddenValues (user-updated values)
      // Don't include idValues here to avoid overriding user updates
      const allValues = {
        ...candidateValues,
        ...hiddenValues,
      };

      // Check if there's a specific mapping for this field
      const mappedField = fieldValueMappings[fieldCode];

      // Check allValues first (user updates), then fall back to idValues (extracted values)
      // Check mappedField first if it exists, then fieldCode, then baseCode
      if (mappedField) {
        if (mappedField in allValues && allValues[mappedField] !== '') {
          return allValues[mappedField];
        }
        if (mappedField in idValues && idValues[mappedField] !== '') {
          return idValues[mappedField];
        }
      }

      // Check fieldCode and baseCode in allValues first, then idValues
      if (fieldCode in allValues && allValues[fieldCode] !== '') {
        return allValues[fieldCode];
      }
      if (baseCode in allValues && allValues[baseCode] !== '') {
        return allValues[baseCode];
      }
      if (baseCode in idValues && idValues[baseCode] !== '') {
        return idValues[baseCode];
      }
      if (fieldCode in idValues && idValues[fieldCode] !== '') {
        return idValues[fieldCode];
      }

      return '';
    },
    [idValues, fieldValueMappings, candidateValues, hiddenValues],
  );

  // Evaluate field conditions using current group values (idValues) and form values
  const evaluateFieldVisibility = useCallback(
    (field, groupValues, formValues = {}) => {
      if (!field.conditions) return true;

      // Combine candidateValues, hiddenValues, current idValues, and form values for condition evaluation
      const allValues = {
        ...candidateValues,
        ...hiddenValues,
        ...groupValues,
        ...formValues, // Include current form values from React Hook Form
      };

      return evaluateFieldConditions(field.conditions, allValues, {});
    },
    [candidateValues, hiddenValues],
  );

  const candidateDob = hiddenValues?.dob ?? candidateValues?.dob;

  useEffect(() => {
    if (!isConfirmed || !candidateDob) {
      return;
    }
    const dobFields = (displayFields || []).flatMap(field => [field, ...(field.subFields || [])]);
    const idDobChanged = dobFields.some(field => {
      if (!field.code?.endsWith('_dob')) {
        return false;
      }
      const idDob = fieldValues[field.code] || getFieldValue(field.code, field.baseCode);
      return (
        !!idDob && !moment(idDob, 'DD/MM/YYYY').isSame(moment(candidateDob, 'DD/MM/YYYY'), 'day')
      );
    });
    if (idDobChanged) {
      setIsConfirmed(false);
    }
  }, [candidateDob, isConfirmed, displayFields, fieldValues, getFieldValue]);

  // Register/unregister pending confirmation when ExtractedFieldsDisplay is visible
  // Use isConfirmed state (not just confirmedByCand prop) so that when user changes values
  // after confirming, we re-register the pending confirmation
  useEffect(() => {
    if (displayFields?.length > 0 && !isConfirmed) {
      // Register this instance as needing confirmation
      registerPendingConfirmation(confirmationIdRef.current);
    } else {
      // Unregister if fields are no longer displayed or already confirmed
      unregisterPendingConfirmation(confirmationIdRef.current);
    }

    // Cleanup: unregister when component unmounts
    return () => {
      unregisterPendingConfirmation(confirmationIdRef.current);
    };
  }, [displayFields, isConfirmed, registerPendingConfirmation, unregisterPendingConfirmation]);

  // Initialize form with extracted data when idValues change
  useEffect(() => {
    // Only run if we have both idValues data AND displayFields
    if (didInitRef.current) {
      return;
    }
    if (displayFields?.length > 0) {
      didInitRef.current = true;
      const initialValues = {};

      const processFields = fields => {
        for (const field of fields || []) {
          if (field.code) {
            const value = getFieldValue(field.code, field.baseCode);
            if (value !== '') {
              initialValues[field.code] = value;
            }
          }
          if (field.subFields) {
            processFields(field.subFields);
          }
          const altIdGroups = field.altIdFields || field.alt_id_fields;
          if (Array.isArray(altIdGroups)) {
            altIdGroups.forEach(group => processFields(group.subFields || group.sub_fields || []));
          }
        }
      };

      processFields(displayFields);
      reset(initialValues);
    }
  }, [displayFields, idValues, reset, getFieldValue]);

  // Build idUploadDetailsField mappings when displayFields change
  useEffect(() => {
    const mappings = {};

    const processFields = fields => {
      for (const field of fields || []) {
        if (field.idUploadDetailsField) {
          mappings[field.code] = field.idUploadDetailsField;
        }
        if (field.subFields) {
          processFields(field.subFields);
        }
        const altIdGroups = field.altIdFields || field.alt_id_fields;
        if (Array.isArray(altIdGroups)) {
          altIdGroups.forEach(group => processFields(group.subFields || group.sub_fields || []));
        }
      }
    };

    processFields(displayFields);
    setIdUploadDetailMappings(mappings);
  }, [displayFields]);

  const mapIdValuesToBaseCodes = (displayFields, idValues) => {
    if (!Array.isArray(displayFields) || !displayFields.length || !idValues) return {};

    const subFieldsToEntries = subs =>
      (subs || []).map(sub => [sub?.code, sub?.baseCode]).filter(([code, base]) => code && base);
    const codeToBaseMap = Object.fromEntries(
      displayFields.flatMap(field => {
        const fromSubFields = Array.isArray(field?.subFields)
          ? subFieldsToEntries(field.subFields)
          : [];
        const fromAltId = (field?.altIdFields || field?.alt_id_fields || []).flatMap(group =>
          subFieldsToEntries(group?.subFields || group?.sub_fields),
        );
        return [...fromSubFields, ...fromAltId];
      }),
    );

    return Object.fromEntries(
      Object.entries(idValues).map(([key, value]) => [codeToBaseMap[key] || key, value ?? null]),
    );
  };

  const handleConfirmDetails = useCallback(
    async data => {
      try {
        setIsSubmitting(true);
        // Process hidden dependencies before saving
        const allValues = {
          ...candidateValues,
          ...hiddenValues,
          ...data, // Include the form data being submitted
        };

        const hiddenDeps = getDependentsFromValues(allValues, hiddenDependencyCodes, {
          includeIndexes: false, // hidden fields don't have indexes
          ignoreEmpty: true,
        });

        if (hiddenDeps.length > 0) {
          const hiddenUpdates = runHiddenDependents(hiddenDeps, allValues, hiddenFields);
          if (hiddenUpdates) {
            dispatch(updateHiddenValues(hiddenUpdates));
          }
        }

        // First, try to save to id_upload_detail if we have an uploadId
        if (uploadId) {
          // Map extracted fields to id_upload_detail fields using pre-built mappings
          const idUploadDetailFields = {};
          Object.entries(data).forEach(([fieldCode, value]) => {
            const idUploadDetailsField = idUploadDetailMappings[fieldCode];
            if (idUploadDetailsField) {
              idUploadDetailFields[idUploadDetailsField] = value;
            }
          });

          const mappedIdValues = mapIdValuesToBaseCodes(displayFields, data);
          const { changed } = hasAnyChange(idValues, mappedIdValues);
          // Update id_upload_detail with confirmed_by_cand flag
          try {
            await dispatch(
              updateIdUploadDetail({
                token,
                uploadId,
                data: {
                  ...idUploadDetailFields,
                  cand_fields_overridden: changed,
                  confirmed_by_cand: true,
                },
              }),
            ).unwrap(); // This will throw if the action is rejected
          } catch (error) {
            console.error('Error updating id_upload_detail:', error);
          }
        }

        // Merge candidateValues and data first
        let updatedCandidateValues = {
          ...candidateValues,
          ...data,
        };

        // Check visibility for all displayFields and remove values if conditions are not met
        const processFieldsForVisibility = fields => {
          for (const field of fields || []) {
            if (field.code) {
              // Check if field should be visible using idValues and form data
              const isVisible = evaluateFieldVisibility(field, idValues, data);

              // If field is not visible, remove its value from updatedCandidateValues
              if (!isVisible && field.code in updatedCandidateValues) {
                delete updatedCandidateValues[field.code];
              }
            }

            if (field.subFields) {
              processFieldsForVisibility(field.subFields);
            }
            const altIdGroups = field.altIdFields || field.alt_id_fields;
            if (Array.isArray(altIdGroups)) {
              altIdGroups.forEach(group =>
                processFieldsForVisibility(group.subFields || group.sub_fields || []),
              );
            }
          }
        };

        processFieldsForVisibility(displayFields);

        // If id_upload_detail save succeeded (or no uploadId), proceed with candidateValues
        // Update local state - save as sectionless values
        dispatch(
          updateCandidateValues({
            updatedCandidateValues,
          }),
        );

        // Also save to backend
        dispatch(
          saveCandidateChecksValues({
            token,
            data: {
              values: updatedCandidateValues,
            },
          }),
        );

        toast.custom(
          t => (
            <CustomToast
              t={t}
              title="Thank you for confirming your details!"
              message="Your details have been saved successfully."
              type="success"
            />
          ),
          {
            duration: 5000,
          },
        );
        setIsConfirmed(true);
        onConfirm();
        previousValuesRef.current = data;
        return true;
      } catch (error) {
        toast.custom(
          t => (
            <CustomToast
              t={t}
              title="Oops - something went wrong"
              message={
                error?.message ||
                error ||
                'Please try again or proceed to the next step. You can email us at team@checkmate.tech if you need help.'
              }
              type="error"
            />
          ),
          {
            duration: 5000,
          },
        );
        return false;
      } finally {
        // Registration lifecycle is owned by the isConfirmed-driven effect above, not here.
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      }
    },
    [
      dispatch,
      token,
      candidateValues,
      uploadId,
      idUploadDetailMappings,
      hiddenValues,
      idValues,
      hiddenDependencyCodes,
      hiddenFields,
    ],
  );

  // Memoize the main render logic
  const renderedFields = useMemo(() => {
    if (!displayFields || displayFields?.length === 0) {
      return null;
    }

    return (displayFields || [])?.map((field, index) => {
      // Handle nested structure - if field has subFields, render them
      if (field.subFields && field.subFields.length > 0) {
        return (
          <div key={`field_${index}`} className="d-flex flex-column u-gap-large">
            {(field?.subFields || [])?.map((subField, subIndex) => {
              // Check if subfield should be visible using current group values and form values
              const isVisible = evaluateFieldVisibility(subField, idValues, fieldValues);
              if (!isVisible) return null;

              const question = {
                id: subField.code || subField.id || `sub_field_${index}_${subIndex}`,
                type: subField.type || 'text',
                text: subField.title || '',
                description: subField.description || '',
                required: subField.required,
                ...subField,
              };

              const fieldKey = subField.code || subField.name || subField.id;

              return (
                <div key={question.id} className="d-flex flex-column u-gap-small">
                  <QuestionWrapper
                    question={question}
                    children={question.children}
                    fieldName={fieldKey}
                    control={control}
                    register={register}
                    setValue={setValue}
                    trigger={trigger}
                    getValues={getMergedValues}
                    componentMap={localComponentMap}
                    requirementVariantStyle="text"
                    gap="small"
                    titleSize="small"
                    noMargin
                    getComponentProps={value => getComponentProps(value, question)}
                    allValues={fieldValues}
                    error={errors[fieldKey]?.message || ''}
                    isVisible={true}
                  />
                </div>
              );
            })}
          </div>
        );
      }

      // Handle regular fields
      // Check if field should be visible using current group values and form values
      const isVisible = evaluateFieldVisibility(field, idValues, fieldValues);
      if (!isVisible) return null;

      const question = {
        id: field.code || field.id || field.name || `display_field_${index}`,
        type: field.type || 'text',
        text: field.label || field.name || field.code || `Field ${index + 1}`,
        description: field.description || '',
        required: Boolean(field.required),
        ...field,
      };

      const fieldKey = field.code || field.name || field.id;

      return (
        <div key={question.id} className="d-flex flex-column u-gap-small">
          <QuestionWrapper
            question={question}
            fieldName={fieldKey}
            control={control}
            register={register}
            setValue={setValue}
            trigger={trigger}
            getValues={getMergedValues}
            componentMap={localComponentMap}
            getComponentProps={value => getComponentProps(value, question)}
            allValues={fieldValues}
            requirementVariantStyle="text"
            gap="small"
            titleSize="small"
            noMargin
            error={errors[fieldKey]?.message || ''}
            isVisible={true}
            {...(question.type === 'select' &&
              field.options && {
                children: field.options.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.name || option.label}
                  </Select.Item>
                )),
              })}
          />
        </div>
      );
    });
  }, [
    displayFields,
    idValues,
    getFieldValue,
    localComponentMap,
    control,
    register,
    setValue,
    trigger,
    getMergedValues,
    getComponentProps,
    fieldValues,
    errors,
    brand,
    evaluateFieldVisibility,
  ]);

  const getExternalIssues = () => {
    const fieldsByCode = {};
    const collectFields = fields => {
      for (const field of fields || []) {
        if (field.code) fieldsByCode[field.code] = field;
        collectFields(field.subFields);
        (field.altIdFields || field.alt_id_fields || []).forEach(group =>
          collectFields(group.subFields || group.sub_fields),
        );
      }
    };
    collectFields(displayFields);

    const issuesByTarget = {};
    Object.keys(errors).forEach(errorCode => {
      const field = fieldsByCode[errorCode];
      if (!field?.validatorName || errors[errorCode]?.type !== field.validatorName) return;
      getValidatorDependencyCodes(field.validatorName, field.validationParams).forEach(
        fieldCode => {
          if (fieldsByCode[fieldCode]) return;
          const location = getFieldLocation(fieldCode, fieldPathIndex, sections);
          if (location) {
            issuesByTarget[`${location.sectionCode}.${fieldCode}`] = { ...location, fieldCode };
          }
        },
      );
    });
    return Object.values(issuesByTarget);
  };

  const externalIssues = getExternalIssues();

  const getSectionTitle = sectionCode =>
    t(`candidate_fields.${sectionCode}`, { returnObjects: true })?.title ||
    sections.find(section => section.code === sectionCode)?.title ||
    sectionCode;

  const handleFixIssue = issue => {
    setSheetOpen(false);
    navigate(buildNavPath(buildFieldPath({ ...issue, token }), admin));
  };

  if (!renderedFields) {
    return null;
  }

  const handleSheetConfirm = handleSubmit(async data => {
    const success = await handleConfirmDetails(data);
    if (success) {
      setSheetOpen(false);
    }
  });

  return (
    <div className="d-flex flex-column u-gap-medium">
      {isConfirmed ? (
        <div className="flex items-center gap-2" style={brandingStyleVariables}>
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            size={20}
            className="text-green-2 shrink-0 leading-none"
          />
          <span className="text-green-2 text-lg font-regular leading-none">
            {t('components.extractedFieldsDisplay.detailsConfirmed')}
          </span>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="cursor-pointer border-0 bg-transparent p-0 text-lg font-regular leading-none underline"
            style={{ color: 'var(--link-color)' }}
            data-testid="update-details-button">
            {t('components.extractedFieldsDisplay.updateDetails')}
          </button>
        </div>
      ) : (
        <CustomButton
          onClick={() => setSheetOpen(true)}
          brand={brand}
          dataTestId="review-confirm-details-button">
          {t('components.extractedFieldsDisplay.reviewAndConfirmDetails')}
        </CustomButton>
      )}

      <ConfirmDetailsSheet
        // Unconfirmed: only the oldest pending confirmation may show its sheet, to avoid stacking.
        open={
          isConfirmed ? sheetOpen : sheetOpen && isActiveConfirmation(confirmationIdRef.current)
        }
        title={t('components.extractedFieldsDisplay.reviewYourDetails', {
          documentName: documentTitle,
        })}
        description={t('components.extractedFieldsDisplay.reviewYourDetailsDescription', {
          documentName: documentTitle,
        })}
        dataTestId="confirm-id-details-sheet"
        canClose={externalIssues.length > 0}
        onClose={() => setSheetOpen(false)}
        footer={
          <>
            <CustomButton
              onClick={handleSheetConfirm}
              brand={brand}
              disabled={isSubmitting}
              dataTestId="confirm-details-button">
              {isSubmitting
                ? t('components.extractedFieldsDisplay.confirming')
                : t('components.extractedFieldsDisplay.confirmDetails')}
            </CustomButton>
            {externalIssues.map(issue => (
              <CustomButton
                key={`${issue.sectionCode}.${issue.fieldCode}`}
                type="secondary"
                onClick={() => handleFixIssue(issue)}
                brand={brand}
                marginTop={false}
                dataTestId={`fix-${issue.fieldCode}-button`}>
                {t('components.extractedFieldsDisplay.goToSectionToFix', {
                  section: getSectionTitle(issue.sectionCode),
                })}
              </CustomButton>
            ))}
            {externalIssues.length > 0 && (
              <CustomButton
                type="secondary"
                onClick={() => setSheetOpen(false)}
                brand={brand}
                marginTop={false}
                dataTestId="close-details-sheet-button">
                {t('components.extractedFieldsDisplay.closeAndFixLater')}
              </CustomButton>
            )}
          </>
        }>
        <div className="d-flex flex-column u-gap-medium">{renderedFields}</div>
      </ConfirmDetailsSheet>
    </div>
  );
};

ExtractedFieldsDisplay.propTypes = {
  displayFields: PropTypes.array,
  idValues: PropTypes.object,
  brand: PropTypes.object,
  uploadId: PropTypes.number,
  documentTitle: PropTypes.string,
  confirmedByCand: PropTypes.bool,
  onConfirm: PropTypes.func,
};
